import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import AcedModal from "../../components/essentials/modals/aced-modal/AcedModal";
import { Search } from "../../components/header/SearchIcon";
import { selectUser, setUser } from "../../core/auth/authSlice";
import {
  addMessage,
  editLastMessage,
  removeLastMessage,
} from "../../core/auth/chatSlice";
import { User } from "../../shared/models";
import { useWebSocket } from "../../shared/socket-provider/WebSocketProvider";
import ChatList from "./chat-list/ChatList";
import { IMessage } from "./components/message/Message";
import "./Messages.scss";

const Messages: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser) as User;
  const location = useLocation();

  const socket = useWebSocket();

  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (newMessage: IMessage) => {
      if (newMessage.type === "system") {
        if (newMessage.receiver === user?.actorId) {
          dispatch(addMessage({ chatId: newMessage.chatId, message: newMessage }));
        }
      } else if (newMessage.sender !== user?.actorId) {
        dispatch(addMessage({ chatId: newMessage.chatId, message: newMessage }));
      }
    };

    const handleEditLastMessage = (editedMessage: IMessage) => {
      dispatch(editLastMessage(editedMessage));
    };

    const handleDeleteLastMessage = (payload: { chatId: string; messageId: string }) => {
      dispatch(removeLastMessage(payload));
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("editLastMessage", handleEditLastMessage);
    socket.on("deleteLastMessage", handleDeleteLastMessage);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("editLastMessage", handleEditLastMessage);
      socket.off("deleteLastMessage", handleDeleteLastMessage);
    };
  }, [socket, user?.actorId, dispatch]);

  const isChatOpen = location.pathname !== "/messages";

  return (
    <div className="messages__container">
      <div className={`chatList-outlet ${isChatOpen ? "chatList-outlet--hidden" : ""}`}>
        <ChatList />
      </div>
      <Outlet />
    </div>
  );
};

export default Messages;

export const EmptyChat: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  return (
    <div className="empty-chat">
      <h1>{t("messages.yourMessages")}</h1>
      <p>{t("messages.selectChatPrompt")}</p>
      <p>{t("messages.or")}</p>
      <button
        className="btn btn-primary"
        onClick={() => {
          setModalOpen(true);
        }}
      >
        {t("common.buttons.startNewChat")}
      </button>
      <NewMessageModal
        isModalOpen={isModalOpen}
        setModalOpen={setModalOpen}
      ></NewMessageModal>
    </div>
  );
};

export interface NewMessageModalProps {
  isModalOpen: any;
  setModalOpen: any;
}
export const NewMessageModal: React.FC<NewMessageModalProps> = ({
  isModalOpen,
  setModalOpen,
}) => {
  return (
    <>
      <AcedModal
        state={{ isModalOpen, setModalOpen }}
        className="new-message-modal"
        title="New Message"
      >
        <Search action="message" onClose={() => setModalOpen(false)} />
      </AcedModal>
    </>
  );
};
